// Pricing2.js - Contains functions for creating a horizontal pricing comparison section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a horizontal Pricing comparison section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the pricing section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The pricing section elements
 */
export const createPricingElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const pricingSectionClass = 'pricing2-section';
  const pricingContainerClass = 'pricing2-container';
  const pricingHeaderClass = 'pricing2-header';
  const pricingTitleClass = 'pricing2-title';
  const pricingSubtitleClass = 'pricing2-subtitle';
  const pricingCardsClass = 'pricing2-cards';
  const pricingCardClass = 'pricing2-card';
  const pricingCardHeaderClass = 'pricing2-card-header';
  const pricingCardTitleClass = 'pricing2-card-title';
  const pricingCardPriceClass = 'pricing2-card-price';
  const pricingFeaturesClass = 'pricing2-features';
  const pricingFeatureClass = 'pricing2-feature';
  const pricingFeatureCheckClass = 'pricing2-feature-check';
  const pricingFeatureXClass = 'pricing2-feature-x';
  const pricingButtonClass = 'pricing2-button';
  const pricingFeaturedCardClass = 'pricing2-featured-card';
  const pricingFeaturedTagClass = 'pricing2-featured-tag';
  
  // Desktop Styles (default)
  const pricingSectionStyles = {
    position: 'relative',
    padding: '80px 20px',
    backgroundColor: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const pricingContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '40px'
  };

  const pricingHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '10px',
    width: '100%',
    maxWidth: '700px'
  };

  const pricingTitleStyles = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '16px'
  };

  const pricingSubtitleStyles = {
    fontSize: '18px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '20px'
  };

  const pricingCardsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
    gap: '20px'
  };

  const pricingCardStyles = {
    flex: '1',
    minWidth: '280px',
    maxWidth: '350px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    border: '1px solid #f0f0f0'
  };

  const pricingCardHeaderStyles = {
    padding: '24px 16px',
    backgroundColor: '#f8f9fa',
    textAlign: 'center',
    borderBottom: '1px solid #f0f0f0'
  };

  const pricingCardTitleStyles = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '8px'
  };

  const pricingCardPriceStyles = {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '8px'
  };

  const pricingFeaturesStyles = {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    gap: '12px',
    flex: '1'
  };

  const pricingFeatureStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    color: '#666666'
  };

  const pricingFeatureCheckStyles = {
    color: '#10b981',
    fontWeight: 'bold'
  };

  const pricingFeatureXStyles = {
    color: '#ef4444',
    fontWeight: 'bold'
  };

  const pricingButtonStyles = {
    padding: '12px 24px',
    margin: '20px',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    border: '1px solid #e0e0e0',
    backgroundColor: '#ffffff',
    color: '#333333'
  };

  const pricingFeaturedCardStyles = {
    transform: 'scale(1.03)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    borderTop: '3px solid #3b82f6',
    borderColor: '#f0f7ff'
  };

  const pricingFeaturedTagStyles = {
    position: 'absolute',
    top: '0',
    right: '20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    padding: '5px 10px',
    fontSize: '12px',
    fontWeight: 'bold',
    borderRadius: '0 0 5px 5px',
    zIndex: '1'
  };

  // Tablet Styles
  const tabletPricingCardsStyles = {
    justifyContent: 'center',
    gap: '15px'
  };

  // Mobile Styles
  const mobilePricingSectionStyles = {
    padding: '60px 15px'
  };

  const mobilePricingCardsStyles = {
    flexDirection: 'column',
    alignItems: 'center'
  };

  const mobilePricingCardStyles = {
    width: '100%',
    maxWidth: '350px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(pricingSectionClass, pricingSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingContainerClass, pricingContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingHeaderClass, pricingHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTitleClass, pricingTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingSubtitleClass, pricingSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardsClass, pricingCardsStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardClass, pricingCardStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardHeaderClass, pricingCardHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardTitleClass, pricingCardTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCardPriceClass, pricingCardPriceStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeaturesClass, pricingFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureClass, pricingFeatureStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureCheckClass, pricingFeatureCheckStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureXClass, pricingFeatureXStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingButtonClass, pricingButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeaturedCardClass, pricingFeaturedCardStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeaturedTagClass, pricingFeaturedTagStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(pricingCardsClass, tabletPricingCardsStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(pricingSectionClass, mobilePricingSectionStyles);
    newRegistry[DEVICES.MOBILE].set(pricingCardsClass, mobilePricingCardsStyles);
    newRegistry[DEVICES.MOBILE].set(pricingCardClass, mobilePricingCardStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    pricingSectionClass,
    pricingContainerClass,
    pricingHeaderClass,
    pricingTitleClass,
    pricingSubtitleClass,
    pricingCardsClass,
    pricingCardClass,
    pricingCardHeaderClass,
    pricingCardTitleClass,
    pricingCardPriceClass,
    pricingFeaturesClass,
    pricingFeatureClass,
    pricingFeatureCheckClass,
    pricingFeatureXClass,
    pricingButtonClass,
    pricingFeaturedCardClass,
    pricingFeaturedTagClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  
  // Header elements
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  
  // Cards container
  const cardsContainerId = Date.now() + 5;
  
  // Basic card elements
  const basicCardId = Date.now() + 6;
  const basicHeaderId = Date.now() + 7;
  const basicTitleId = Date.now() + 8;
  const basicPriceId = Date.now() + 9;
  const basicFeaturesId = Date.now() + 10;
  const basicFeature1Id = Date.now() + 11;
  const basicFeature2Id = Date.now() + 12;
  const basicFeature3Id = Date.now() + 13;
  const basicFeature4Id = Date.now() + 14;
  const basicFeature5Id = Date.now() + 15;
  const basicButtonId = Date.now() + 16;
  
  // Pro card elements
  const proCardId = Date.now() + 17;
  const proHeaderId = Date.now() + 18;
  const proTitleId = Date.now() + 19;
  const proPriceId = Date.now() + 20;
  const proFeaturesId = Date.now() + 21;
  const proFeature1Id = Date.now() + 22;
  const proFeature2Id = Date.now() + 23;
  const proFeature3Id = Date.now() + 24;
  const proFeature4Id = Date.now() + 25;
  const proFeature5Id = Date.now() + 26;
  const proButtonId = Date.now() + 27;
  const proTagId = Date.now() + 28;
  
  // Enterprise card elements
  const enterpriseCardId = Date.now() + 29;
  const enterpriseHeaderId = Date.now() + 30;
  const enterpriseTitleId = Date.now() + 31;
  const enterprisePriceId = Date.now() + 32;
  const enterpriseFeaturesId = Date.now() + 33;
  const enterpriseFeature1Id = Date.now() + 34;
  const enterpriseFeature2Id = Date.now() + 35;
  const enterpriseFeature3Id = Date.now() + 36;
  const enterpriseFeature4Id = Date.now() + 37;
  const enterpriseFeature5Id = Date.now() + 38;
  const enterpriseButtonId = Date.now() + 39;
  
  const newElements = [
    // Main Pricing Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Pricing Section',
      parentId: null,
      className: pricingSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'pricing-section',
        'data-component': 'section',
        'data-section-type': 'pricing',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: pricingContainerClass,
      styles: {},
      children: [headerId, cardsContainerId],
      text: '',
      attributes: {
        'data-element': 'pricing-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'pricing-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Pricing Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: pricingTitleClass,
      styles: {},
      children: [],
      text: 'Compare Our Plans',
      attributes: {
        'data-element': 'pricing-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Pricing Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: pricingSubtitleClass,
      styles: {},
      children: [],
      text: 'Choose the features and capabilities that are right for your business needs. All plans include a 30-day free trial.',
      attributes: {
        'data-element': 'pricing-subtitle'
      }
    },
    
    // Cards Container
    {
      id: cardsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Cards',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardsClass,
      styles: {},
      children: [basicCardId, proCardId, enterpriseCardId],
      text: '',
      attributes: {
        'data-element': 'pricing-cards'
      }
    },
    
    // Basic Card
    {
      id: basicCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardClass,
      styles: {},
      children: [basicHeaderId, basicFeaturesId, basicButtonId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'basic'
      }
    },
    
    // Basic Card Header
    {
      id: basicHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Card Header',
      parentId: basicCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardHeaderClass,
      styles: {},
      children: [basicTitleId, basicPriceId],
      text: '',
      attributes: {
        'data-element': 'pricing-card-header'
      }
    },
    
    // Basic Card Title
    {
      id: basicTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Basic Card Title',
      parentId: basicHeaderId,
      hierarchyName: getHierarchyName('h3'),
      className: pricingCardTitleClass,
      styles: {},
      children: [],
      text: 'Basic',
      attributes: {
        'data-element': 'pricing-card-title'
      }
    },
    
    // Basic Card Price
    {
      id: basicPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Card Price',
      parentId: basicHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPriceClass,
      styles: {},
      children: [],
      text: '$19/mo',
      attributes: {
        'data-element': 'pricing-card-price'
      }
    },
    
    // Basic Features
    {
      id: basicFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Features',
      parentId: basicCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeaturesClass,
      styles: {},
      children: [basicFeature1Id, basicFeature2Id, basicFeature3Id, basicFeature4Id, basicFeature5Id],
      text: '',
      attributes: {
        'data-element': 'pricing-features'
      }
    },
    
    // Basic Feature 1
    {
      id: basicFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 1',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ 1 Website',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Basic Feature 2
    {
      id: basicFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 2',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ 10GB Storage',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Basic Feature 3
    {
      id: basicFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 3',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ Custom Domain',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Basic Feature 4
    {
      id: basicFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 4',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✗ Advanced Analytics',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Basic Feature 5
    {
      id: basicFeature5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Basic Feature 5',
      parentId: basicFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✗ Priority Support',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Basic Button
    {
      id: basicButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Basic Button',
      parentId: basicCardId,
      hierarchyName: getHierarchyName('button'),
      className: pricingButtonClass,
      styles: {},
      children: [],
      text: 'Get Started',
      attributes: {
        'data-element': 'pricing-button'
      }
    },
    
    // Pro Card
    {
      id: proCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: `${pricingCardClass} ${pricingFeaturedCardClass}`,
      styles: {
        position: 'relative'
      },
      children: [proHeaderId, proFeaturesId, proButtonId, proTagId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'pro',
        'data-featured': 'true'
      }
    },
    
    // Pro Featured Tag
    {
      id: proTagId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Featured Tag',
      parentId: proCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeaturedTagClass,
      styles: {},
      children: [],
      text: 'POPULAR',
      attributes: {
        'data-element': 'pricing-featured-tag'
      }
    },
    
    // Pro Card Header
    {
      id: proHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card Header',
      parentId: proCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardHeaderClass,
      styles: {
        backgroundColor: '#f0f7ff'
      },
      children: [proTitleId, proPriceId],
      text: '',
      attributes: {
        'data-element': 'pricing-card-header'
      }
    },
    
    // Pro Card Title
    {
      id: proTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Pro Card Title',
      parentId: proHeaderId,
      hierarchyName: getHierarchyName('h3'),
      className: pricingCardTitleClass,
      styles: {},
      children: [],
      text: 'Pro',
      attributes: {
        'data-element': 'pricing-card-title'
      }
    },
    
    // Pro Card Price
    {
      id: proPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Card Price',
      parentId: proHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPriceClass,
      styles: {},
      children: [],
      text: '$49/mo',
      attributes: {
        'data-element': 'pricing-card-price'
      }
    },
    
    // Pro Features
    {
      id: proFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Features',
      parentId: proCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeaturesClass,
      styles: {},
      children: [proFeature1Id, proFeature2Id, proFeature3Id, proFeature4Id, proFeature5Id],
      text: '',
      attributes: {
        'data-element': 'pricing-features'
      }
    },
    
    // Pro Feature 1
    {
      id: proFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 1',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ 5 Websites',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 2
    {
      id: proFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 2',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ 50GB Storage',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 3
    {
      id: proFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 3',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ Custom Domain',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 4
    {
      id: proFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 4',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ Advanced Analytics',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Feature 5
    {
      id: proFeature5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pro Feature 5',
      parentId: proFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✗ Priority Support',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Pro Button
    {
      id: proButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Pro Button',
      parentId: proCardId,
      hierarchyName: getHierarchyName('button'),
      className: pricingButtonClass,
      styles: {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        border: '1px solid #3b82f6'
      },
      children: [],
      text: 'Get Started',
      attributes: {
        'data-element': 'pricing-button'
      }
    },
    
    // Enterprise Card
    {
      id: enterpriseCardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardClass,
      styles: {},
      children: [enterpriseHeaderId, enterpriseFeaturesId, enterpriseButtonId],
      text: '',
      attributes: {
        'data-element': 'pricing-card',
        'data-plan': 'enterprise'
      }
    },
    
    // Enterprise Card Header
    {
      id: enterpriseHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Card Header',
      parentId: enterpriseCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardHeaderClass,
      styles: {},
      children: [enterpriseTitleId, enterprisePriceId],
      text: '',
      attributes: {
        'data-element': 'pricing-card-header'
      }
    },
    
    // Enterprise Card Title
    {
      id: enterpriseTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Enterprise Card Title',
      parentId: enterpriseHeaderId,
      hierarchyName: getHierarchyName('h3'),
      className: pricingCardTitleClass,
      styles: {},
      children: [],
      text: 'Enterprise',
      attributes: {
        'data-element': 'pricing-card-title'
      }
    },
    
    // Enterprise Card Price
    {
      id: enterprisePriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Card Price',
      parentId: enterpriseHeaderId,
      hierarchyName: getHierarchyName('div'),
      className: pricingCardPriceClass,
      styles: {},
      children: [],
      text: '$99/mo',
      attributes: {
        'data-element': 'pricing-card-price'
      }
    },
    
    // Enterprise Features
    {
      id: enterpriseFeaturesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Features',
      parentId: enterpriseCardId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeaturesClass,
      styles: {},
      children: [enterpriseFeature1Id, enterpriseFeature2Id, enterpriseFeature3Id, enterpriseFeature4Id, enterpriseFeature5Id],
      text: '',
      attributes: {
        'data-element': 'pricing-features'
      }
    },
    
    // Enterprise Feature 1
    {
      id: enterpriseFeature1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 1',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ Unlimited Websites',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Enterprise Feature 2
    {
      id: enterpriseFeature2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 2',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ 500GB Storage',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Enterprise Feature 3
    {
      id: enterpriseFeature3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 3',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ Custom Domain',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Enterprise Feature 4
    {
      id: enterpriseFeature4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 4',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ Advanced Analytics',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Enterprise Feature 5
    {
      id: enterpriseFeature5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enterprise Feature 5',
      parentId: enterpriseFeaturesId,
      hierarchyName: getHierarchyName('div'),
      className: pricingFeatureClass,
      styles: {},
      children: [],
      text: '✓ Priority Support',
      attributes: {
        'data-element': 'pricing-feature'
      }
    },
    
    // Enterprise Button
    {
      id: enterpriseButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Enterprise Button',
      parentId: enterpriseCardId,
      hierarchyName: getHierarchyName('button'),
      className: pricingButtonClass,
      styles: {},
      children: [],
      text: 'Contact Us',
      attributes: {
        'data-element': 'pricing-button'
      }
    }
  ];
  
  return {
    pricingSection: newElements[0],
    elements: newElements
  };
};

export default createPricingElements; 